import { useState, useRef, useCallback, useEffect } from "react";
import cn from "classnames";

import { useDebounce } from "hooks/useDebounce";
import IMagnifier from "assets/i-magnifier.js";
import IArrowRight1 from "assets/i-arrow-right.js";

const Option = ({ search, currentVal, lastItemRef, slug, ...props }) => {
  return (
    <div>
      <button
        ref={lastItemRef}
        className="w-full font-inter flex items-center justify-between text-[13px] text-left"
        {...props}
      >
        {currentVal}

        <div className="w-4 h-4 flex items-center justify-center">
          <IArrowRight1 color="#686A68" width={16} height={16} />
        </div>
      </button>

      <div className="border-[#DEDFDE] w-full h-[1px] my-2 border-t-[0px] border-x-[0px] border-b-[0.5px] border-dashed border-[#DEDFDE]" />
    </div>
  );
};

const RenderOptions = ({
  data,
  value,
  onSelect,
  lastItemRef,
  isFetching,
  startTyping,
  noDataMessage = "Data tidak ditemukan",
  loadingMessage = "Sedang mencari...",
}) => {
  return (
    <div className="absolute top-[calc(100%+4px)] left-0 right-0 bg-white border border-solid border-[#D8D8D9] rounded-[8px] shadow-[0px_4px_12px_rgba(0,0,0,0.1)] z-50">
      <div className="max-h-[420px] overflow-y-auto scrollbar-hide p-4 py-[12px]">
        <div className="border-b border-solid border-[#DEDFDE] pb-[12px] mb-[12px]">
          <p className="text-[11px] text-[#686A68]">Cari sebagai keyword:</p>

          <div
            onClick={() => onSelect({ name: value, slug: "Keyword" })}
            className="flex items-center justify-between"
          >
            <p className="text-[13px] font-inter text-[#252526]">{value}</p>
            <IArrowRight1 color="#686A68" width={16} height={16} />
          </div>
        </div>
        <p className="text-[11px] text-[#686A68] mb-1">Cari sebagai area:</p>
        {data?.map((item, idx) => (
          <Option
            onClick={() => onSelect(item)}
            key={`${item.id}-${idx}`}
            slug={item.slug}
            search={value}
            currentVal={item.name}
          />
        ))}

        {data.length > 0 && (
          <div
            ref={lastItemRef}
            style={{ height: "5px", width: "100%", margin: "5px 0" }}
          />
        )}

        {!data.length && value && !isFetching && (
          <div
            onClick={() => onSelect({ name: value, slug: "Keyword" })}
            className="flex items-center justify-between gap-2 border-b-[0.5px] border-dashed border-[#DEDFDE] py-4 cursor-pointer hover:bg-[#F9F9F9] transition-colors"
          >
            <p className="font-inter text-[17px] leading-[23.8px] tracking-[0.17px] text-[#040704]">
              {value}
            </p>

            <div className="flex items-center gap-2">
              <p className="bg-[#EBF0F7] rounded-[4px] px-2 py-0.5 font-inter text-[11px] leading-[15.6px] tracking-[0.11px] text-[#3268AE]">
                Kata Kunci
              </p>
              <div className="w-4 h-4 flex items-center justify-center">
                <IArrowRight1 width={16} height={16} />
              </div>
            </div>
          </div>
        )}

        {isFetching && (
          <div className="flex items-center gap-2 justify-between py-4 border-b-[0.5px] border-dashed border-[#DEDFDE]">
            <p className="font-inter text-[14px] leading-[19.6px] text-[#040704]">
              {loadingMessage}
            </p>
            <div className="flex gap-1">
              <div
                className="w-2 h-2 bg-[#040704] rounded-full animate-bounce"
                style={{ animationDelay: "0ms" }}
              ></div>
              <div
                className="w-2 h-2 bg-[#040704] rounded-full animate-bounce"
                style={{ animationDelay: "150ms" }}
              ></div>
              <div
                className="w-2 h-2 bg-[#040704] rounded-full animate-bounce"
                style={{ animationDelay: "300ms" }}
              ></div>
            </div>
          </div>
        )}

        {!isFetching && startTyping && data?.length === 0 && (
          <div className="flex items-start gap-4 justify-between py-4">
            <p className="font-inter text-[14px] leading-[19.6px] text-[#040704]">
              {noDataMessage}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const SearchDropdown = ({
  delay = 500,
  onChange,
  onSelect,
  onClear,
  containerClassName,
  inputClassName,
  placeholder = "Cari...",
  data = [],
  isFetching = false,
  hasNextPage = false,
  fetchNextPage,
  noDataMessage,
  loadingMessage,
  ...props
}) => {
  const ref = useRef(null);
  const observer = useRef();
  const [search, setSearch] = useState("");
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const { debounceFn } = useDebounce({
    onChange: (query) => {
      onChange?.(query);
    },
    delay,
  });

  const lastItemRef = useCallback(
    (node) => {
      if (isFetching) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage?.();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isFetching, hasNextPage, fetchNextPage],
  );

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsOpenDropdown(false);
    }
  };

  const handleClear = () => {
    setSearch("");
    setIsOpenDropdown(false);
    onClear?.();
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={ref} className="relative w-full">
      <div
        className={cn(
          "h-[24px] p-2 flex items-center gap-1 rounded-[4px] border-[1px] border-solid border-[#D8D8D9] w-full",
          containerClassName,
          props.disabled && "opacity-50 cursor-not-allowed",
        )}
      >
        <IMagnifier />
        <input
          className={cn(
            "h-[24px] py-2 text-[11px] leading-[17px] tracking-[-0.4px] font-inter text-[#252526] w-full placeholder:text-[#808285]",
            inputClassName,
          )}
          onChange={(e) => {
            setSearch(e.target.value);
            debounceFn(e.target.value);
          }}
          onFocus={() => setIsOpenDropdown(true)}
          value={search}
          placeholder={placeholder}
          {...props}
        />
        {search && (
          <button
            onClick={handleClear}
            className="flex items-center justify-center w-4 h-4 rounded-full bg-[#FD4949] hover:bg-[#E03E3E] transition-colors cursor-pointer flex-shrink-0"
            type="button"
          >
            <svg
              width="8"
              height="8"
              viewBox="0 0 8 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1 1L7 7M7 1L1 7"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        )}
      </div>

      {isOpenDropdown && (
        <RenderOptions
          data={data}
          value={search}
          onSelect={(item) => {
            onSelect?.(item);
            setIsOpenDropdown(false);
            setSearch(item?.name ?? "");
          }}
          lastItemRef={lastItemRef}
          isFetching={isFetching}
          startTyping={!!search}
          noDataMessage={noDataMessage}
          loadingMessage={loadingMessage}
        />
      )}
    </div>
  );
};

export default SearchDropdown;
